import { ckmeans } from "simple-statistics";
import { AppleCountFeatureCollection } from "../layer.types";

export type YieldBucket = {
  categoryIndex: number;
  min: number;
  max: number;
};

type CountedFeature = {
  properties?: Record<string, unknown> | null;
};

const BUCKET_COUNT = 5;

export type AppleThresholdOptions = {
  property?: string;
  bucketCount?: number;
};

export function findBucket(
  buckets: YieldBucket[],
  count: number,
): YieldBucket | undefined {
  return (
    buckets.find((bucket) => count >= bucket.min && count <= bucket.max) ??
    buckets.findLast((bucket) => count >= bucket.min) ??
    buckets.at(0)
  );
}
export function calculateAppleThresholds(
  features: readonly CountedFeature[],
  {
    property = "apple_count",
    bucketCount = BUCKET_COUNT,
  }: AppleThresholdOptions = {},
): YieldBucket[] {
  const counts = features
    .map((feature) => feature.properties?.[property])
    .filter((count) => count != null)
    .map(Number)
    .filter((count) => !isNaN(count));

  if (counts.length === 0) return [];

  // Edge case: If you have fewer unique data points than desired buckets,
  // Jenks will fail, so we fall back to the original linear logic.
  const uniqueCounts = new Set(counts).size;
  const targetBucketCount = Math.min(bucketCount, uniqueCounts);

  if (targetBucketCount === 0) return [];

  // Sort the counts ascending (ckmeans requires sorted input for performance)
  const sortedCounts = [...counts].sort((a, b) => a - b);

  const clusters = ckmeans(sortedCounts, targetBucketCount);

  return clusters.map((cluster, index) => {
    const min = Math.floor(cluster[0]);
    const max = Math.floor(cluster[cluster.length - 1]);

    return {
      categoryIndex: index,
      min,
      max,
    };
  });
}

type CalibratedFeature = AppleCountFeatureCollection["features"][number] & {
  properties: { calibrated_count: number };
};

export function getAverageAppleCountPerTree(
  geojson: AppleCountFeatureCollection,
): number {
  const validFeatures = geojson.features.filter(
    (feature): feature is CalibratedFeature =>
      feature.properties.plot_id != null &&
      feature.properties.calibrated_count != null,
  );
  const numberOfPlots = validFeatures.length;
  const sumAppleCount = validFeatures.reduce(
    (acc, feature) => acc + feature.properties.calibrated_count,
    0,
  );

  const averageAppleCountPerTree =
    numberOfPlots === 0 ? 0 : sumAppleCount / numberOfPlots;

  return averageAppleCountPerTree;
}
